import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useCandidateAuth } from "@/contexts/CandidateAuthContext";
import { Loader2, AlertCircle, CheckCircle } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";

export default function CandidateVerifyPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { login } = useCandidateAuth();
  const [status, setStatus] = useState("verifying"); // verifying | success | error
  const [error, setError] = useState("");

  useEffect(() => {
    const token = searchParams.get("token");

    if (!token) {
      setStatus("error");
      setError("No magic link token provided");
      return;
    }

    verifyMagicLink(token);
  }, [searchParams]);

  const verifyMagicLink = async (token) => {
    try {
      // Backend sets httpOnly session cookie automatically
      const response = await api.get(`/candidate/verify-magic-link?token=${encodeURIComponent(token)}`);
      login(response.data.candidate, response.data.token, response.data.csrf_token);
      setStatus("success");
      setTimeout(() => {
        navigate("/candidate/dashboard", { replace: true });
      }, 1000);
    } catch (err) {
      console.error("Verification failed:", err);
      setStatus("error");
      setError(err.response?.data?.detail || "Invalid or expired magic link");
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <Card className="max-w-md w-full">
        <CardContent className="pt-6 text-center">
          {status === "verifying" && (
            <div className="space-y-4">
              <Loader2 className="w-12 h-12 mx-auto animate-spin text-primary" />
              <h2 className="text-xl font-semibold">Verifying Magic Link...</h2>
              <p className="text-sm text-muted-foreground">
                Please wait while we log you in
              </p>
            </div>
          )}

          {status === "success" && (
            <div className="space-y-4">
              <CheckCircle className="w-12 h-12 mx-auto text-success" />
              <h2 className="text-xl font-semibold text-success">Login Successful!</h2>
              <p className="text-sm text-muted-foreground">
                Redirecting to your dashboard...
              </p>
            </div>
          )}

          {status === "error" && (
            <div className="space-y-4">
              <AlertCircle className="w-12 h-12 mx-auto text-destructive" />
              <h2 className="text-xl font-semibold text-destructive">Login Failed</h2>
              <p className="text-sm text-muted-foreground">{error}</p>
              <div className="pt-4 space-y-2">
                <Button
                  onClick={() => navigate("/candidate/login")}
                  className="w-full"
                  data-testid="request-new-link-btn"
                >
                  Request New Magic Link
                </Button>
                <Button
                  variant="outline"
                  onClick={() => navigate("/")}
                  className="w-full"
                >
                  Back to Home
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
